// src/shared/providers/ThemeProvider.tsx
'use client';
import { useEffect, useMemo } from 'react';
import ConfigProvider from 'antd/es/config-provider';
import { useUIStore } from '../store/ui.store';
import { lightTheme, theme } from '@/shared/lib/ant';
import { useTranslation } from 'react-i18next';
import arAnt from 'antd/lib/locale/ar_EG';
import enAnt from 'antd/lib/locale/en_US';
import type { Locale } from 'antd/lib/locale';

const languageAnt: Record<string, Locale> = { en: enAnt, ar: arAnt };

type ThemeProviderProps = {
  children: React.ReactNode;
  initialTheme?: 'light' | 'dark';
};

export default function ThemeProvider({ children, initialTheme }: ThemeProviderProps) {
  const selectedTheme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const { i18n } = useTranslation();
  const locale = useMemo(() => languageAnt[i18n.language] || enAnt, [i18n.language]);

  // Inicialización (localStorage o prop)
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    const effective = (stored ?? initialTheme) as 'dark' | 'light' | undefined;
    if (effective === 'dark' || effective === 'light') setTheme(effective);
  }, [setTheme, initialTheme]);

  // Sincroniza la clase .tw-on-dark en <body>
  useEffect(() => {
    const body = document.body;
    if (!body.classList.contains('tw')) body.classList.add('tw');
    if (selectedTheme === 'dark') body.classList.add('tw-on-dark');
    else body.classList.remove('tw-on-dark');
  }, [selectedTheme]);

  return (
    <ConfigProvider locale={locale} theme={selectedTheme === 'dark' ? theme : lightTheme}>
      {children}
    </ConfigProvider>
  );
}
